import { useState } from 'react';
import { Box, Tabs, Tab, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Replay5 as Replay5Icon,
  Forward5 as Forward5Icon,
  Repeat as RepeatIcon,
} from '@mui/icons-material';
import { TranscriptEditor } from '../transcripts/TranscriptEditor';
import { TranslationEditor } from '../translations/TranslationEditor';
import { VideoPlayerVocabularyPanel } from '../../pages/Projects/VideoPlayer/VideoPlayerVocabularyPanel';
import type { Segment } from '../../types/api.types';
import { useTranslation } from 'react-i18next';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`segment-tabpanel-${index}`}
      aria-labelledby={`segment-tab-${index}`}
    >
      <Box sx={{ pt: 1 }}>{children}</Box>
    </div>
  );
}

interface SegmentEditorTabsProps {
  segment?: Segment;
  projectId: number;
  episodeId: number;
  projectLanguage: string;
  userLanguage: string;
  hasTranscript: boolean;
  /** All segments for the episode, used to label segment chips in the vocabulary tab. */
  segments?: Segment[];
  /** Called when a segment chip is clicked in the vocabulary tab. Also switches to Transcript tab. */
  onSegmentClick?: (segmentId: number) => void;
  /** Fired when the user starts working with the transcript — pins the segment boundary. */
  onSegmentWorked?: () => void;
  /** Fired when the user leaves transcript edit mode — releases the segment boundary. */
  onExitEditMode?: () => void;
  isPlaying?: boolean;
  isLooping?: boolean;
  onPlay?: () => void;
  onLoop?: () => void;
  onSkipBackward?: () => void;
  onSkipForward?: () => void;
}

export const SegmentEditorTabs = ({ 
  segment, 
  projectId, 
  episodeId,
  projectLanguage,
  userLanguage,
  hasTranscript,
  segments,
  onSegmentClick,
  onSegmentWorked,
  onExitEditMode,
  isPlaying = false,
  isLooping = false,
  onPlay,
  onLoop,
  onSkipBackward,
  onSkipForward,
}: SegmentEditorTabsProps) => {
  const { t } = useTranslation('segments');
  const tPlayer = useTranslation('player').t;
  const [activeTab, setActiveTab] = useState(0);

  // When a segment chip is clicked from the vocabulary tab, select it and switch to Transcript
  const handleSegmentChipClick = (segmentId: number) => {
    onSegmentClick?.(segmentId);
    setActiveTab(0);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ width: '100%' }} data-tour="segment-editor">
      <Stack direction="row" alignItems="center" sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          aria-label="segment editor tabs"
          sx={{ 
            flex: 1,
            minHeight: '40px',
            '& .MuiTab-root': {
              minHeight: '40px',
              py: 1,
            }
          }}
        >
          <Tab 
            label="Transcription" 
            id="segment-tab-0" 
            aria-controls="segment-tabpanel-0"
          />
          <Tab 
            label="Translation" 
            id="segment-tab-1" 
            aria-controls="segment-tabpanel-1"
          />
          <Tab
            label={tPlayer('vocabularyTab', 'Vocabulary')}
            id="segment-tab-2"
            aria-controls="segment-tabpanel-2"
          />
        </Tabs>
      </Stack>

      {/* Vocabulary tab — episode-level, no audio controls needed */}
      {activeTab === 2 && (
        <VideoPlayerVocabularyPanel
          projectId={projectId}
          episodeId={episodeId}
          projectLanguage={projectLanguage}
          userLanguage={userLanguage}
          hasTranscript={hasTranscript}
          segments={segments}
          onSegmentClick={handleSegmentChipClick}
        />
      )}

      {/* Transcription / Translation tabs — segment-level with audio controls */}
      {activeTab !== 2 && (
        <>
          {/* Audio Controls */}
          <Box sx={{
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.default',
            px: 2,
            py: 1,
          }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title={t('skipBackward')}>
                <span>
                  <IconButton
                    aria-label="skip-backward"
                    onClick={onSkipBackward}
                    size="small"
                    disabled={!onSkipBackward}
                    sx={{ minWidth: 44, minHeight: 44 }}
                  >
                    <Replay5Icon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={isPlaying ? t('pause') : t('playOnce')}>
                <span>
                  <IconButton
                    aria-label={isPlaying ? 'pause' : 'play'}
                    onClick={onPlay}
                    size="small"
                    disabled={!onPlay}
                    sx={{ minWidth: 44, minHeight: 44 }}
                  >
                    {isPlaying ? <PauseIcon /> : <PlayIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('skipForward')}>
                <span>
                  <IconButton
                    aria-label="skip-forward"
                    onClick={onSkipForward}
                    size="small"
                    disabled={!onSkipForward}
                    sx={{ minWidth: 44, minHeight: 44 }}
                  >
                    <Forward5Icon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('loopContinuously')}>
                <span>
                  <IconButton
                    aria-label="loop"
                    aria-pressed={isLooping}
                    onClick={onLoop}
                    size="small"
                    disabled={!onLoop}
                    data-tour="loop-button"
                    sx={{
                      minWidth: 44,
                      minHeight: 44,
                      ...(isLooping && {
                        bgcolor: 'primary.light',
                        color: 'primary.main',
                        '&:hover': { bgcolor: 'primary.light' },
                      }),
                    }}
                  >
                    <RepeatIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Box>

          {segment ? (
            <>
              <TabPanel value={activeTab} index={0}>
                <TranscriptEditor
                  segment={segment}
                  projectId={projectId}
                  episodeId={episodeId}
                  onSegmentWorked={onSegmentWorked}
                  onExitEditMode={onExitEditMode}
                />
              </TabPanel>

              <TabPanel value={activeTab} index={1}>
                <TranslationEditor
                  segment={segment}
                  projectId={projectId}
                  episodeId={episodeId}
                />
              </TabPanel>
            </>
          ) : (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ py: 4, px: 2, textAlign: 'center' }}
            >
              <Typography variant="body2" color="text.secondary">
                {tPlayer('selectSegmentHint', 'Select a segment above to study transcript and translation here')}
              </Typography>
            </Stack>
          )}
        </>
      )}
    </Box>
  );
};
